import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/modern/components/ui/dialog';
import { Button } from '@/modern/components/ui/button';
import { X, Calendar, Tag, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export interface StatusChangeDetails {
  label: string;
  date: string;
  updatedBy?: string;
}

interface StatusChangeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statusChanges: StatusChangeDetails[];
}

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  open,
  onOpenChange,
  statusChanges
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[--swivu-bryntum-z-index] max-w-[400px] max-h-[85vh] flex flex-col p-0 shadow-none">
        <DialogHeader className="px-6 pt-6 pb-2 sticky top-0 bg-background z-10 border-0">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-base font-normal break-words min-w-0 flex-1">
              {statusChanges.length === 1 ? statusChanges[0].label : `Status Changes (${statusChanges.length})`}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0 hover:bg-muted shrink-0"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto px-6 pt-2 pb-6 space-y-4">
          {statusChanges.length === 1 ? (
            // Single status change - no card wrapper
            <div className="space-y-2">
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {format(parseISO(statusChanges[0].date), 'EEEE, MMMM d, yyyy')}
                  </span>
                </div>

                {statusChanges[0].updatedBy && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>Updated by {statusChanges[0].updatedBy}</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Multiple status changes - keep card structure
            statusChanges.map((statusChange, index) => (
              <div
                key={index}
                className="border border-border rounded-lg p-4 bg-card"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 min-w-0">
                      <h3 className="text-base font-semibold text-foreground break-words min-w-0 flex-1">
                        {statusChange.label}
                      </h3>
                    </div>

                    <div className="border-t border-border pt-2 mt-2 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {format(parseISO(statusChange.date), 'EEEE, MMMM d, yyyy')}
                        </span>
                      </div>

                      {statusChange.updatedBy && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="h-4 w-4" />
                          <span>Updated by {statusChange.updatedBy}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {statusChanges.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No status changes found</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatusChangeModal;
